import { Card, CardContent } from "../../../components/ui/card";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Label,
  Tooltip,
} from "recharts";
import { DataTable } from "../../../components/ui/data-table";
import { CustomTooltip, renderCustomizedLabel } from "./ChartHelpers";
import { useContext, useEffect, useState } from "react";
import {
  AccountingContext,
  PieChartData,
} from "../../../providers/AccountingProvider";
import { formatMonthYear, formatUniversalNumber } from "../../../lib/utils";
import { TableSkeleton } from "../../../components/Skeleton/TableSkeleton";
import PieChartSkeleton from "../../../components/Skeleton/PieChartSkeleton";
import { PieChartIcon } from "lucide-react";
import { UIContext } from "../../../providers/UIProvider";
import useIsSmallScreen from "../../../hooks/useIsSmallScreen";

// Generate N shades of green using HSL
function getGreenShade(index: number, total: number) {
  // Hue 120 = green, lower saturation for softer color, vary lightness for distinction
  const minLight = 40;
  const maxLight = 75;
  const lightness =
    minLight + ((maxLight - minLight) * index) / Math.max(1, total - 1);
  return `hsl(120, 45%, ${lightness}%)`;
}

export const Revenue = () => {
  const isSmallScreen = useIsSmallScreen(1366);
  const { revenueData, currentDate, filteredTotalRevenue, isSummaryLoading } =
    useContext(AccountingContext);

  const { theme } = useContext(UIContext);
  const [currentTheme, setCurrentTheme] = useState(theme);
  useEffect(() => {
    const handleStorageChange = () => {
      setCurrentTheme(theme);
    };

    window.addEventListener("storage", handleStorageChange);

    const handleThemeChange = (
      event: CustomEvent<{ theme: "dark" | "light" }>
    ) => {
      setCurrentTheme(event.detail.theme);
    };

    window.addEventListener("themeChange", handleThemeChange as EventListener);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener(
        "themeChange",
        handleThemeChange as EventListener
      );
    };
  }, []);

  const pieChartData: PieChartData[] =
    revenueData?.map((item) => ({
      name: item.category,
      value: Math.abs(item.amount),
      percentage: item.value,
      originalAmount: item.amount,
    })) || [];

  const filteredData = (pieChartData || [])?.filter((item) => {
    return item?.originalAmount > 0;
  });
  const columns = [
    {
      accessorKey: "category",
      header: "Category",
    },
    {
      accessorKey: "type",
      header: "Type",
    },
    {
      accessorKey: "amount",
      header: () => (
        <span style={{ width: "70px" }} className="text-right w-full">
          Amount
        </span>
      ),
      cell: ({ row }: { row: { original: { amount: number } } }) => {
        return (
          <span style={{ width: "70px" }} className="text-right block">
            {formatUniversalNumber(Number(row.original.amount))}
          </span>
        );
      },
    },
    {
      accessorKey: "value",
      header: () => (
        <span style={{ width: "50px" }} className="text-right w-full">
          Value
        </span>
      ),
      cell: ({ row }: any) => {
        const Value: number = row.original.value;
        return (
          <span style={{ width: "50px" }} className="text-right block">
            {Value}
          </span>
        );
      },
    },
  ];

  return (
    <div className={`h-full flex flex-col`}>
      <Card className="h-[650px] flex flex-col BookEZ_card_bg shadow-lg border BookEZ_card_border_color BookEZ_card_radius">
        <div className="flex justify-between items-center p-4 border-b BookEZ_card_border_color">
          <div className="flex items-center gap-3">
            <div className="w-1.5 h-10 BookEZ_revenue_bg rounded-full" />
            <h3 className="BookEZ_font_bold BookEZ_font_header_color">
              Revenue
            </h3>
          </div>
          <span className="BookEZ_font_body_size BookEZ_font_color_gray_2 font-normal">
            {formatMonthYear(currentDate)}
          </span>
        </div>
        <div className="h-[500px] relative p-5">
          <ResponsiveContainer width="100%" height="100%">
            {isSummaryLoading ? (
              <PieChartSkeleton />
            ) : revenueData && revenueData.length === 0 ? (
              <Card className="w-full max-w-lg mx-auto">
                <CardContent className="flex flex-col items-center justify-center h-[400px] p-8">
                  <div className="relative mb-8">
                    <div className="w-24 h-24 rounded-full BookEZ_card_bg_secondary_light flex items-center justify-center">
                      <PieChartIcon className="w-12 h-12 BookEZ_font_color_gray_2" />
                    </div>
                  </div>
                  <h2 className="BookEZ_font_body_large BookEZ_font_bold BookEZ_font_color_gray_2 mb-3">
                    No Revenue Data Available
                  </h2>
                  <p className="BookEZ_font_body_size  BookEZ_font_color_gray_2 text-center mb-8 max-w-[280px] leading-relaxed">
                    We couldn't find any revenue records for your selected time
                    period. Try adjusting your filters or add new data.
                  </p>
                </CardContent>
              </Card>
            ) : (
              <PieChart>
                <Pie
                  data={filteredData}
                  innerRadius={80}
                  outerRadius={120}
                  dataKey="value"
                  startAngle={90}
                  endAngle={450}
                  minAngle={10}
                  label={(props) => {
                    if (isSmallScreen) return null;
                    if (props.originalAmount < 0) return null;
                    return renderCustomizedLabel({
                      ...props,
                      fill: getGreenShade(props.index, filteredData.length),
                      theme,
                    });
                  }}
                  labelLine={false}
                >
                  {filteredData.map((entry, index) => (
                    <Cell
                      key={`revenue-cell-${index}`}
                      fill={getGreenShade(index, filteredData.length)}
                      className="transition-all duration-200 hover:opacity-80"
                    />
                  ))}
                  <Label
                    content={({ viewBox }) => {
                      if (!viewBox) return null;
                      const cx = (viewBox as any).cx ?? 0;
                      const cy = (viewBox as any).cy ?? 0;
                      return (
                        <text
                          x={cx}
                          y={cy}
                          textAnchor="middle"
                          dominantBaseline="middle"
                        >
                          <tspan
                            x={cx}
                            y={cy - 10}
                            className="BookEZ_font_bold BookEZ_font_body_medium"
                            fill={
                              currentTheme === "light" ? "#374151" : "#ffffff"
                            }
                          >
                            Total Revenue
                          </tspan>
                          <tspan
                            x={cx}
                            y={cy + 10}
                            className="BookEZ_font_bold BookEZ_font_light_bold"
                            fill={
                              currentTheme === "light" ? "#111827" : "#ffffff"
                            }
                          >
                            {formatUniversalNumber(filteredTotalRevenue)}
                          </tspan>
                        </text>
                      );
                    }}
                  />
                </Pie>
                <Tooltip content={<CustomTooltip />} />
              </PieChart>
            )}
          </ResponsiveContainer>
        </div>
        <div className="flex flex-wrap gap-4 p-6">
          {filteredData &&
            filteredData.map((entry, index) => (
              <div key={index} className="flex items-center gap-2">
                <div
                  className="w-3 h-3 rounded-full"
                  style={{
                    backgroundColor:
                      // REVENUE_COLORS[index % REVENUE_COLORS.length],
                      getGreenShade(index, filteredData.length),
                  }}
                />
                <div className="flex flex-col">
                  <span className="BookEZ_font_body_size BookEZ_font_color_gray_2">
                    {entry.name}
                  </span>
                </div>
              </div>
            ))}
        </div>
      </Card>
      {revenueData && revenueData.length > 0 && (
        <div className={`mt-4`}>
          {isSummaryLoading ? (
            <TableSkeleton />
          ) : (
            <DataTable
              className="min-w-[400px]!"
              data={revenueData || []}
              columns={columns}
              showPagination={false}
              variant="striped"
              size="sm"
              cellPadding="lg"
            />
          )}
        </div>
      )}
    </div>
  );
};
